/**
 * Static reference data for the AI Toolkit section.
 *
 * These are the internal AI tools (the Bug Fix agent + the MCP servers) built by
 * the team. This is brief reference/enablement content — edit here and redeploy
 * to update.
 *
 * Categories drive both the Overview grouping and the category tabs.
 */

export const CATEGORIES = [
  { key: 'agents', label: 'Agents' },
  { key: 'mcp', label: 'MCP Servers' },
];

/**
 * @typedef {Object} AiTool
 * @property {string} id            Stable slug.
 * @property {string} name          Display name.
 * @property {string} category      One of CATEGORIES[].key.
 * @property {string} typeLabel     Short type badge text.
 * @property {string} status        Maturity badge text.
 * @property {string} oneLiner      Single-sentence summary (Overview card).
 * @property {string} whatItDoes    2-4 sentences of detail.
 * @property {Array<{text?: string, code?: string}>} howToUse  Steps; `code` renders as a copyable block.
 * @property {string} tech          Key tech/stack.
 * @property {string} integrations  External systems it connects to.
 * @property {string} repo          Repo path / location.
 */

/**
 * Claude Code CLI + Cursor mcp.json snippets for a remote HTTP MCP.
 * @param {{ name: string, url: string, bearer?: boolean }} opts
 * @returns {Array<{text?: string, code?: string}>}
 */
function mcpConnect({ name, url, bearer = false }) {
  const headerFlag = bearer
    ? ' --header "Authorization: Bearer <GITHUB_PAT>"'
    : '';
  const cursorHeaders = bearer
    ? `,
      "headers": {
        "Authorization": "Bearer <GITHUB_PAT>"
      }`
    : '';
  return [
    {
      text: 'Claude Code — add the remote HTTP MCP (use the same PAT the deploy uses where a bearer is required):',
      code: `claude mcp add --transport http ${name} ${url}${headerFlag}`,
    },
    {
      text: 'Cursor — Settings → Cursor Settings → MCP → New MCP Server, or merge this into the project `.cursor/mcp.json` (or `~/.cursor/mcp.json` for all projects). Restart Cursor after saving.',
      code: `{
  "mcpServers": {
    "${name}": {
      "url": "${url}"${cursorHeaders}
    }
  }
}`,
    },
  ];
}

/** @type {AiTool[]} */
export const AI_TOOLS = [
  {
    id: 'bug-fix-agent',
    name: 'Bug Fix Agent',
    category: 'agents',
    typeLabel: 'Agent',
    status: 'Active',
    oneLiner:
      'Claude Code multi-agent workflow that takes an ADO bug ID and auto-diagnoses the root cause, writes a regression test, applies a minimal fix, and opens a linked GitHub PR — with human approval gates.',
    whatItDoes:
      'Given an Azure DevOps bug ID (and optional target repos), it runs a sequence of specialised single-responsibility agents: fetch bug details, find root cause (git-blame to spot the introducing commit), write a JUnit 5 regression test, apply and compile a minimal fix, validate no other tests regress, commit/push, open a GitHub PR, and post root-cause + impact comments back to the ADO ticket. It maintains a per-repo knowledge base (module maps, gotchas, build quirks, bug-fix log) that agents read to orient quickly. It runs interactively (three inline approval gates) or headless in CI, where approvals are ADO state transitions.',
    howToUse: [
      { text: 'This workflow is Claude Code CLI commands (`/bug-fix`). In Cursor, connect the supporting MCP servers (highq-code-kb, highq-kb) via the snippets on those cards — the slash commands themselves run in Claude Code.' },
      { text: 'Prerequisites: Claude Code CLI, ADO + GitHub MCP servers, and local clones of the target repos (listed in repos.yaml).' },
      { text: 'Index a repo once:', code: '/repo-index <repo_name>' },
      { text: 'Fix a bug (approve at each of the three gates):', code: '/bug-fix <bug_id> [repo_name ...]' },
      { text: 'CI mode: triggered from an ADO Service Hook; approvals are driven by transitioning the ADO Custom.AgentState field.' },
    ],
    tech: 'Prompt/Markdown-driven agent definitions orchestrated by Claude Code (Anthropic models). CI in GitHub Actions. Targets Java repos (JUnit 5, Gradle/Maven).',
    integrations: 'Azure DevOps, GitHub, highq-code-kb + highq-kb MCP servers, Microsoft Teams notifications.',
    repo: 'highq_bug-fix-agent',
  },
  {
    id: 'highq-code-kb',
    name: 'highq-code-kb',
    category: 'mcp',
    typeLabel: 'MCP Server',
    status: 'Operational',
    oneLiner:
      'Turns the team’s repositories into queryable per-domain code knowledge graphs and serves them over MCP so assistants can search code and trace architecture. (Graphify)',
    whatItDoes:
      'Clones a manifest of target repos, parses each locally with tree-sitter into a per-repo knowledge graph, then merges them by domain "bucket" (integrations, isheet, apps, platform, services, coco) per branch. Each bucket graph is exposed as an HTTP MCP endpoint for structural queries and code search, with a router endpoint that fans a single query across all buckets. It also provides PR/branch impact tools. Note: graphs are merged by union with no cross-repo edges — it supports in-repo structure plus cross-domain search, not true cross-repo impact traversal.',
    howToUse: [
      ...mcpConnect({
        name: 'highq-code-kb',
        url: 'https://lab1metricsportal.highq.com/highq-code/mcp',
        bearer: true,
      }),
      { text: 'Use /highq-code (router, cross-domain) or /highq-code-<bucket> for a scoped, faster single domain; -dev variants target the develop branch.' },
      { text: 'PR tools: list_prs, get_pr_impact, triage_prs. Add repos via repos.tsv, then run the Deploy + Intake workflows.' },
    ],
    tech: 'Python 3.12, tree-sitter, the graphifyy package, MCP SDK + uvicorn. Graphs are JSON files loaded in memory (no graph DB). LLM/semantic backend off by default (zero egress).',
    integrations: 'GitHub (repo cloning + PR data via gh CLI), metrics-portal / nginx proxy, MCP clients (Claude Code, Cursor), optional Anthropic API for the semantic backend.',
    repo: 'highq-graphify-kb',
  },
  {
    id: 'highq-kb',
    name: 'highq-kb',
    category: 'mcp',
    typeLabel: 'MCP Server',
    status: 'Operational',
    oneLiner:
      'Self-hosted MCP server that exposes HighQ knowledge-base documentation to AI agents over an authenticated endpoint.',
    whatItDoes:
      'A deployment wrapper around the highq-knowledge-base MCP server. On boot it clones the KB repo’s live-docs branch into a persisted volume and serves that documentation through MCP so agents can query HighQ knowledge-base docs. Content stays fresh via a GitHub push webhook (/refresh) plus a polling fallback. It deploys on its own cadence, decoupled from the metrics-portal app.',
    howToUse: [
      ...mcpConnect({
        name: 'highq-kb',
        url: 'https://lab1metricsportal.highq.com/highq-kb-mcp/mcp',
        bearer: true,
      }),
      { text: 'Endpoints: /mcp (bearer-auth MCP), /refresh (webhook → git pull), /healthz (liveness).' },
      { text: 'Deployed via the "Deploy highq-kb-mcp" GitHub Actions workflow.' },
    ],
    tech: 'Node.js MCP server (streamable-HTTP), Docker Compose, nginx reverse proxy, GitHub Actions.',
    integrations: 'GitHub (private tr/highq_knowledge_base content repo + push webhook), metrics-portal webapp/nginx proxy, MCP clients (Claude Code, Cursor).',
    repo: 'highq-kb-mcp',
  },
  {
    id: 'highq-sre',
    name: 'highq-sre',
    category: 'mcp',
    typeLabel: 'MCP Server',
    status: 'Operational',
    oneLiner:
      'MCP server that exposes HighQ SRE runbooks, incident write-ups, investigations, and knowledge-base notes to AI agents.',
    whatItDoes:
      'Clones the tr/HighQ_SRE repo on startup and keeps it in sync via a GitHub push webhook (/refresh) plus a polling fallback. It serves markdown from runbooks/, Incidents/, Investigations/, Knowledge/, Onboarding/, and azure-gov/ (configurable). Wiki pages are pre-indexed daily into runbooks/ by the HighQ_SRE runbook-sync workflow. The MCP reads everything from disk — no HighQ API credentials at runtime. Cross-folder tools: list_docs, search_docs, get_doc. Runbooks-only aliases: list_runbooks, search_runbooks, get_runbook.',
    howToUse: [
      ...mcpConnect({
        name: 'highq-sre',
        url: 'https://lab1metricsportal.highq.com/highq-sre-mcp/mcp',
        bearer: true,
      }),
      { text: 'All folders: list_docs([folder]), search_docs(query[, folder]), get_doc(path). folder is one of runbooks, Incidents, Investigations, Knowledge, Onboarding, azure-gov.' },
      { text: 'Runbooks only: list_runbooks([category]), search_runbooks(query[, category]), get_runbook(path).' },
      { text: 'Endpoints: /mcp (bearer-auth MCP), /refresh (webhook → git pull), /healthz (liveness, includes per-folder counts). The previous /highq-runbooks-mcp path still proxies to this server.' },
      { text: 'Deployed via the "Deploy highq-sre-mcp" GitHub Actions workflow. Re-run it after mcp-server changes in HighQ_SRE.' },
    ],
    tech: 'Python 3.12, FastMCP (streamable-HTTP), uvicorn, Docker Compose. File-based — content served from disk, no vector DB or embedding calls.',
    integrations: 'GitHub (tr/HighQ_SRE repo + push webhook), HighQ runbook-sync workflow (daily PR), metrics-portal / nginx proxy, MCP clients (Claude Code, Cursor).',
    repo: 'HighQ_SRE (mcp-server/)',
  },
  {
    id: 'highq-drill4j-mcp',
    name: 'highq-drill4j-mcp',
    category: 'mcp',
    typeLabel: 'MCP Server',
    status: 'Operational',
    oneLiner:
      'Read-only MCP server that tells you which tests are impacted by a change, cross-referencing a git diff against Drill4J’s coverage map.',
    whatItDoes:
      'Computes the minimal set of tests to run for a change: it diffs two refs in a pre-cloned repo to extract changed production methods, then matches those (class, method) pairs against Drill4J’s coverage tables to find every test that historically covered them. Changes can be given as two build refs, a feature/release branch (auto-detecting the fork point), or an ADO work item (AB#<id>), for which it reads the ticket’s linked GitHub commits/PRs. Returns a clean, deduped list of runner case IDs to execute.',
    howToUse: [
      ...mcpConnect({
        name: 'highq-drill4j-mcp',
        url: 'https://lab1drill4j.highq.com/drill4jmcp/mcp',
      }),
      { text: 'Tools exposed: status, list_repos, impacted_tests_for_builds(baseline, target), impacted_tests_for_feature(feature_branch[, base_branch]), impacted_tests_for_ticket(AB#<id>).' },
      { text: 'Cold-start analyses can run several minutes — raise the client tool timeout, e.g.:', code: 'MCP_TOOL_TIMEOUT=600000' },
    ],
    tech: 'Python, FastMCP (streamable-HTTP), Postgres (read-only), git CLI. Runs as a Docker container behind nginx on the Drill4J VM.',
    integrations: 'Drill4J Postgres coverage database, Azure DevOps Boards REST API, local pre-cloned Git repo, MCP clients (Claude Code, Cursor).',
    repo: 'highq_docker_utils/drill4j-mcp',
  },
  {
    id: 'highq-test-bank',
    name: 'highq-test-bank',
    category: 'mcp',
    typeLabel: 'MCP Server',
    status: 'Operational',
    oneLiner:
      'In-tenant semantic search index over ~34K ADO test cases, exposed to AI agents via MCP and to the portal via a REST API.',
    whatItDoes:
      'Pulls Test Case work items from Azure DevOps (via saved WIQL queries), embeds each one locally with a sentence-transformers model, and stores the vectors in Postgres + pgvector to make the corpus semantically searchable. It supports hybrid (vector + keyword) search, near-duplicate detection, coverage-gap analysis, and RAG-style "suggest tests for a change". Ingestion is incremental (content-hash based) and resumable. The vector DB is a search index, not the source of truth — tools return ADO ids/URLs and ADO stays authoritative.',
    howToUse: [
      ...mcpConnect({
        name: 'highq-test-bank',
        url: 'https://lab1metricsportal.highq.com/testcase-vectordb-mcp',
      }),
      { text: 'MCP tools: search_test_cases, find_duplicates, coverage_gaps, suggest_tests_for_change.' },
      { text: 'Also exposes a server-side REST API (POST /search, /duplicates, /coverage; GET /stats) consumed by the metrics-portal.' },
    ],
    tech: 'Python, Postgres 16 + pgvector, local sentence-transformers (BAAI/bge-large-en-v1.5, 1024-dim), FastAPI. Fully in-tenant — no external embedding calls.',
    integrations: 'Azure DevOps (test-case work items), metrics-portal (REST search API), MCP clients (Claude Code, Cursor).',
    repo: 'testcase-vectordb',
  },
];
